<div class="time-off-management-container">
  <div class="time-off-header">
    <div class="title-container">
      <h3 class="section-title">Time Off</h3>
    </div>
    <div class="header-actions">
      <!-- Same .inline-action chip the Resources Directory uses for its print
           control, so the two calendars offer the same affordance. Hidden
           outside the calendar view, where there is nothing to print. -->
      @if (viewMode === "calendar") {
        <div
          class="inline-action calendar-print-button"
          role="button"
          tabindex="0"
          aria-label="Print calendar"
          matTooltip="Print calendar"
          matTooltipPosition="below"
          matTooltipClass="light-tooltip"
          (click)="printCalendar()"
          (keydown.enter)="printCalendar()"
          (keydown.space)="$event.preventDefault(); printCalendar()"
        >
          <mat-icon class="inline-action-icon">print</mat-icon>
          <span>Print</span>
        </div>
      }
      @if (canAddEntry) {
        <button
          mat-stroked-button
          class="add-entry-btn"
          (click)="showAddRow()"
        >
          <mat-icon>add</mat-icon>
          <span>{{
            apiMode() === "self-service"
              ? "Request days off"
              : "Add additional days off"
          }}</span>
        </button>
      }
      <mat-button-toggle-group
        [value]="viewMode"
        (change)="toggleView($event.value)"
        class="view-toggle"
      >
        <mat-button-toggle
          value="calendar"
          matTooltip="Calendar"
          matTooltipPosition="below"
          matTooltipClass="light-tooltip"
        >
          <mat-icon>calendar_month</mat-icon>
        </mat-button-toggle>
        <mat-button-toggle
          value="table"
          matTooltip="List"
          matTooltipPosition="below"
          matTooltipClass="light-tooltip"
        >
          <mat-icon>view_list</mat-icon>
        </mat-button-toggle>
        <mat-button-toggle
          value="balance"
          matTooltip="Balance"
          matTooltipPosition="below"
          matTooltipClass="light-tooltip"
        >
          <mat-icon>account_balance_wallet</mat-icon>
        </mat-button-toggle>
      </mat-button-toggle-group>
    </div>
  </div>

  <div class="time-off-content">
    @if (viewMode === "calendar") {
      <!-- Calendar View - FullCalendar month view -->
      <!-- calendar-print-area marks the only subtree _print.scss keeps visible
           when printing; see calendar-print.utils.ts. -->
      <div class="calendar-wrapper calendar-print-area">
        <div class="calendar-print-caption">
          <span class="calendar-print-caption__title">{{
            employeeName() ? employeeName() + " — Time Off" : "Time Off"
          }}</span>
          <span class="calendar-print-caption__meta"
            >Printed {{ printedOn | date: "mediumDate" }}</span
          >
        </div>
        <full-calendar
          #timeOffCalendar
          [options]="calendarOptions"
        ></full-calendar>
      </div>
    } @else if (viewMode === "balance") {
      <!-- Balance View -->
      <ng-container
        [ngTemplateOutlet]="yearNav"
        [ngTemplateOutletContext]="{
          $implicit: {
            kind: 'balance',
            canPrev: canGoToPreviousBalanceYear(),
            canNext: canGoToNextBalanceYear(),
            caption: isProjectedBalance ? 'Projected' : null,
            value: selectedBalanceYear,
          },
        }"
      ></ng-container>
      @if (isHistoryYear) {
        <!-- Read-only history for a past year (finalized snapshot; no editing) -->
        @if (historyLoading) {
          <div class="empty-state">
            <mat-spinner diameter="24"></mat-spinner>
            <p class="empty-message">Loading history...</p>
          </div>
        } @else if (historyError) {
          <div class="empty-state">
            <mat-icon class="empty-icon">warning</mat-icon>
            <p class="empty-message">{{ historyError }}</p>
          </div>
        } @else if (displayHistoryTypes.length > 0) {
          <div class="table-container">
            <table class="time-off-table balance-summary-table">
              <colgroup>
                <col style="width: 25%" />
                <col />
                <!-- Mirror the current-year layout's actions column so the
                     centered summary sits at the same horizontal position,
                     even though history has no editing controls. -->
                @if (canEditEntitledDays()) {
                  <col style="width: 10%" />
                }
              </colgroup>
              <tbody>
                @for (h of displayHistoryTypes; track h.typeCode) {
                  <tr>
                    <td class="balance-type-cell">
                      {{ getBalanceTypeLabel(h) }}
                    </td>
                    <td class="balance-summary-cell">
                      <span class="balance-summary">
                        @if (h.typeCode === TimeOffTypeCodes.BEREAVEMENT) {
                          <span class="balance-value">{{ h.taken }}</span>
                          bereavement days taken
                        } @else {
                          <span
                            class="balance-value"
                            [ngClass]="getBalanceValueClass(h.remaining)"
                            >{{ h.remaining }}</span
                          >
                          days remaining out of
                          <span
                            class="balance-value"
                            [ngClass]="getBalanceValueClass(h.entitled)"
                            >{{ h.entitled }}</span>
                          ({{ h.taken }} taken)
                        }
                      </span>
                    </td>
                    <!-- Empty cell fills the reserved actions column above. -->
                    @if (canEditEntitledDays()) {
                      <td class="actions-cell"></td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="empty-state">
            <mat-icon class="empty-icon">event_busy</mat-icon>
            <p class="empty-message">
              No history available for {{ selectedBalanceYear }}
            </p>
          </div>
        }
      } @else if (balanceLoading) {
        <div class="empty-state">
          <mat-spinner diameter="24"></mat-spinner>
          <p class="empty-message">Loading balance...</p>
        </div>
      } @else if (balanceError) {
        <div class="empty-state">
          <mat-icon class="empty-icon">warning</mat-icon>
          <p class="empty-message">{{ balanceError }}</p>
        </div>
      } @else if (balance && displayBalances.length > 0) {
        <div class="table-container">
          <table class="time-off-table balance-summary-table">
            <colgroup>
              <col style="width: 25%" />
              <col />
              @if (canEditEntitledDays()) {
                <col style="width: 10%" />
              }
            </colgroup>
            <tbody>
              @for (b of displayBalances; track b.typeCode) {
                <tr>
                  <td class="balance-type-cell">{{ getBalanceTypeLabel(b) }}</td>
                  <td class="balance-summary-cell">
                    <span class="balance-summary">
                      @if (b.typeCode === TimeOffTypeCodes.BEREAVEMENT) {
                        <span class="balance-value">{{
                          b.adjustedDaysTaken
                        }}</span>
                        bereavement days taken
                      } @else {
                        <span
                          class="balance-value"
                          [ngClass]="getBalanceValueClass(b.remaining)"
                          >{{ b.remaining }}</span
                        >
                        days available out of
                        <span
                          class="balance-value"
                          [ngClass]="getBalanceValueClass(b.totalAvailable)"
                          >{{ b.totalAvailable }}</span
                        >
                        ({{ b.proratedEntitled }} + {{ carryoverInTotal(b) }})
                      }
                      @if (getFootnoteMarker(b)) {
                        <button
                          type="button"
                          class="footnote-marker"
                          (click)="openFootnoteDialog(b)"
                          [attr.aria-label]="
                            'Show details for ' + getBalanceTypeLabel(b)
                          "
                        >
                          <mat-icon>help</mat-icon>
                        </button>
                      }
                    </span>
                  </td>
                  @if (canEditEntitledDays()) {
                    <!-- The cell stays on every year so the rows line up, but the
                         button only on the server's current year: the adjustment
                         endpoints accept nothing else, and the dialog would
                         target the year after. -->
                    <td class="actions-cell">
                      @if (canEditSelectedYear) {
                        <button
                          mat-icon-button
                          disableRipple
                          class="details-icon-button"
                          matTooltip="Edit available days or policy"
                          (click)="openPolicyEditDialog(b)"
                        >
                          <mat-icon>more_vert</mat-icon>
                        </button>
                      }
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <div class="empty-state">
          <mat-icon class="empty-icon">event_busy</mat-icon>
          <p class="empty-message">No balance data available</p>
        </div>
      }
    } @else {
      <!-- Table View -->

      <!-- Paging (PBI 6682): the calendar tab's prev/next/today arrangement.
           The default page is every request still to come; each step back is
           one year of ended requests, starting with this year. -->
      <ng-container
        [ngTemplateOutlet]="yearNav"
        [ngTemplateOutletContext]="{
          $implicit: {
            kind: 'list',
            canPrev: canGoToPreviousListYear(),
            canNext: canGoToNextListYear(),
            caption: listYearCaption,
            value: listYearLabel,
          },
        }"
      ></ng-container>

      <!-- Inline Add Form (shown above the table while adding) -->
      @if (isAddingEntry && newEntryForm) {
        <div class="table-container add-form-container" @expandCollapse>
          <table
            class="time-off-table add-form-table"
            [formGroup]="newEntryForm"
          >
            <colgroup>
              <col style="width: 22%" />
              <col style="width: 22%" />
              <col style="width: 22%" />
              <col style="width: 12%" />
              <col style="width: 10%" />
              <col style="width: 12%" />
            </colgroup>
            <thead>
              <tr>
                <th>Start</th>
                <th>End</th>
                <th>Type</th>
                <th class="days-off-header">Days off</th>
                <th class="status-cell">Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr class="inline-add-row">
                <td class="inline-add-cell">
                  <mat-form-field appearance="outline" class="inline-add-field">
                    <input
                      matInput
                      [matDatepicker]="inlineStartPicker"
                      formControlName="displayStartDate"
                      placeholder="mm/dd/yyyy"
                    />
                    <mat-datepicker-toggle
                      matSuffix
                      [for]="inlineStartPicker"
                    ></mat-datepicker-toggle>
                    <mat-datepicker #inlineStartPicker></mat-datepicker>
                  </mat-form-field>
                </td>
                <td class="inline-add-cell">
                  <mat-form-field appearance="outline" class="inline-add-field">
                    <input
                      matInput
                      [matDatepicker]="inlineEndPicker"
                      formControlName="displayEndDate"
                      [min]="getMinEndDate(newEntryForm)"
                      placeholder="mm/dd/yyyy"
                    />
                    <mat-datepicker-toggle
                      matSuffix
                      [for]="inlineEndPicker"
                    ></mat-datepicker-toggle>
                    <mat-datepicker #inlineEndPicker></mat-datepicker>
                  </mat-form-field>
                </td>
                <td class="inline-add-cell">
                  <mat-form-field appearance="outline" class="inline-add-field">
                    <mat-select
                      formControlName="typeLookupValueId"
                      panelWidth="auto"
                      panelClass="time-off-type-select-panel"
                    >
                      @for (type of sortedLookupTypes(); track type.id) {
                        <mat-option [value]="type.id">{{
                          type.name
                        }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </td>
                <td class="days-off-cell">
                  {{ getDaysOff(newEntryForm) }}
                </td>
                <td class="inline-add-notes-cell status-cell">
                  <button
                    mat-icon-button
                    disableRipple
                    class="notes-icon-button inline-add-notes-btn"
                    [disabled]="isNewEntrySaving"
                    [class.has-notes]="getNewEntryHasNotes()"
                    [matTooltip]="getNewEntryHasNotes() ? 'Notes' : 'Add Notes'"
                    (click)="openNewEntryNotesDialog()"
                  >
                    <mat-icon fontSet="material-symbols-outlined">{{
                      getNewEntryHasNotes() ? "article" : "add_notes"
                    }}</mat-icon>
                  </button>
                </td>
                <td class="inline-add-actions-cell">
                  @if (isNewEntrySaving) {
                    <mat-spinner diameter="18"></mat-spinner>
                  } @else {
                    <div class="inline-add-actions">
                      <button
                        mat-icon-button
                        class="inline-save-btn"
                        matTooltip="Save"
                        [disabled]="newEntryForm.invalid"
                        (click)="saveNewEntry()"
                      >
                        <mat-icon>check</mat-icon>
                      </button>
                      <button
                        mat-icon-button
                        class="inline-cancel-btn"
                        matTooltip="Cancel"
                        (click)="cancelAddRow()"
                      >
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                  }
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      }

      @if (filteredEntries.length === 0) {
        <div class="empty-state">
          <mat-icon class="empty-icon">event_busy</mat-icon>
          <p class="empty-message">
            @if (isViewingUpcoming) {
              No upcoming time off
            } @else {
              No time off entries for {{ listPage }}
            }
          </p>
        </div>
      } @else {
        <div class="table-container">
          <table class="time-off-table">
            <colgroup>
              <col style="width: 22%" />
              <col style="width: 22%" />
              <col style="width: 22%" />
              <col style="width: 12%" />
              <col style="width: 10%" />
              <col style="width: 12%" />
            </colgroup>
            <thead>
              <tr>
                <th>Start</th>
                <th>End</th>
                <th>Type</th>
                <th class="days-off-header">Days off</th>
                <th class="status-cell">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (entry of pagedEntries; track entry.id) {
                <tr [class.past-entry-row]="isPastEntry(entry)">
                  <td>{{ formatDisplayDate(entry.displayStartDate) }}</td>
                  <td>{{ formatDisplayDate(entry.displayEndDate) }}</td>
                  <td>{{ getTypeName(entry) }}</td>
                  <td class="days-off-cell">{{ entry.daysOff }}</td>
                  <td class="status-cell">
                    <span
                      class="status-badge"
                      [ngClass]="getStatusBadgeClass(entry)"
                      >{{ getStatusLabel(entry) }}</span
                    >
                  </td>
                  <td class="actions-cell">
                    <button
                      mat-icon-button
                      disableRipple
                      class="details-icon-button"
                      matTooltip="View details"
                      (click)="openDetailsDialog(entry)"
                    >
                      <mat-icon>more_vert</mat-icon>
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
          @if (showPaginator) {
            <mat-paginator
              [length]="filteredEntries.length"
              [pageSize]="pageSize"
              [pageIndex]="pageIndex"
              [pageSizeOptions]="pageSizeOptions"
              showFirstLastButtons
              aria-label="Select page of time off entries"
              (page)="onPageChange($event)"
            ></mat-paginator>
          }
        </div>
      }
    }
  </div>
</div>

<!-- Year navigation shared by the list and balance views (PBI 6682): the
     calendar tab's prev/next arrows, a centred label, and, for the list only,
     the Current pill in the calendar's "today" slot. `nav.kind` routes the
     arrows; the caption renders only when one is given. -->
<ng-template #yearNav let-nav>
  <div class="year-nav">
    <div class="year-nav__controls">
      <div class="year-nav__arrows">
        <button
          type="button"
          class="year-nav-button"
          aria-label="Previous year"
          [disabled]="!nav.canPrev"
          (click)="stepYearNav(nav.kind, -1)"
        >
          <mat-icon>chevron_left</mat-icon>
        </button>
        <button
          type="button"
          class="year-nav-button"
          aria-label="Next year"
          [disabled]="!nav.canNext"
          (click)="stepYearNav(nav.kind, 1)"
        >
          <mat-icon>chevron_right</mat-icon>
        </button>
      </div>
      @if (nav.kind === "list") {
        <button
          type="button"
          class="year-nav-current"
          [disabled]="isViewingUpcoming"
          (click)="goToCurrentListYear()"
        >
          <mat-icon>today</mat-icon>
          <span>Current</span>
        </button>
      }
    </div>
    <div class="year-nav__label">
      @if (nav.caption) {
        <span class="year-nav__caption">{{ nav.caption }}</span>
      }
      <span class="year-nav__value">{{ nav.value }}</span>
    </div>
    <span class="year-nav__spacer"></span>
  </div>
</ng-template>
